import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { appointmentsAPI } from '../../services/api';

export interface Appointment {
  id: string;
  title: string;
  description?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  startTime: string;
  endTime: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  serviceType?: string;
  staffMember?: string;
  resourceId?: string;
  clientId?: string;
  serviceId?: string;
  staffIds?: string[];
  category?: 'normal' | 'exam';
  drivingType?: string;
  licenseCategory?: string;
  price?: number;
  currency?: string;
  notes?: string;
  isRecurring?: boolean;
  recurringPattern?: any;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface AppointmentsState {
  requestId?: string;
  appointments: Appointment[];
  loading: boolean;
  error: string | null;
  filters: {
    all?: boolean;
    view?: 'active' | 'history';
    search?: string;
    status?: string;
    category?: string;
    staffId?: string; clientId?: string; resourceId?: string; licenseCategory?: string; drivingType?: string;
    start?: string;
    end?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const initialState: AppointmentsState = {
  appointments: [],
  loading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const fetchAppointments = createAsyncThunk(
  'appointments/fetchAppointments',
  async (params: {
    page?: number;
    limit?: number;
    all?: boolean;
    view?: 'active' | 'history';
    search?: string;
    status?: string;
    category?: string;
    staffId?: string; clientId?: string; resourceId?: string; licenseCategory?: string; drivingType?: string;
    start?: string;
    end?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const { all, ...query } = params;
      const response = await appointmentsAPI.getAll(query);
      if (all) {
        const records = [...response.data.appointments];
        for (let page = 2; page <= response.data.pagination.pages; page++) {
          const next = await appointmentsAPI.getAll({ ...query, page });
          records.push(...next.data.appointments);
        }
        return { ...response.data, appointments: records };
      }
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Eroare la încărcarea programărilor';
      return rejectWithValue(message);
    }
  }
);

export const fetchAppointment = createAsyncThunk(
  'appointments/fetchAppointment',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await appointmentsAPI.getById(id);
      return response.data.appointment;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Eroare la încărcarea programării';
      return rejectWithValue(message);
    }
  }
);

export const createAppointment = createAsyncThunk(
  'appointments/createAppointment',
  async (appointmentData: Partial<Appointment>, { rejectWithValue }) => {
    try {
      const response = await appointmentsAPI.create(appointmentData);
      return response.data.appointment;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Eroare la crearea programării';
      return rejectWithValue(message);
    }
  }
);

export const updateAppointment = createAsyncThunk(
  'appointments/updateAppointment',
  async ({ id, data }: { id: string; data: Partial<Appointment> }, { rejectWithValue }) => {
    try {
      const response = await appointmentsAPI.update(id, data);
      return response.data.appointment;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Eroare la actualizarea programării';
      return rejectWithValue(message);
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  'appointments/deleteAppointment',
  async (id: string, { rejectWithValue }) => {
    try {
      await appointmentsAPI.delete(id);
      return id;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Eroare la ștergerea programării';
      return rejectWithValue(message);
    }
  }
);

const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AppointmentsState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch appointments
    builder
      .addCase(fetchAppointments.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.appointments = action.payload.appointments;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create appointment
    builder
      .addCase(createAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments.push(action.payload);
        state.error = null;
      })
      .addCase(createAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update appointment
    builder
      .addCase(updateAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppointment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.appointments.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete appointment
    builder
      .addCase(deleteAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = state.appointments.filter(app => app.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters } = appointmentsSlice.actions;
export default appointmentsSlice.reducer;
